import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Eye, Trash2, Mail, Phone, Building2, Package, Calendar } from 'lucide-react';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // Mock data for demonstration
  const mockInquiries = [
    {
      id: '1',
      name: 'Rajesh Kumar',
      company: 'ABC Paints Ltd',
      productRequired: 'Calcite Powder',
      monthlyRequirement: '50 MT',
      phone: '+91 9876543210',
      email: 'rajesh@abcpaints.com',
      message: 'Need 300 mesh calcite powder for paint application',
      createdAt: '2025-02-20T10:30:00',
      status: 'new'
    },
    {
      id: '2',
      name: 'Priya Sharma',
      company: 'XYZ Plastics Pvt Ltd',
      productRequired: 'PCC',
      monthlyRequirement: '100 MT',
      phone: '+91 9123456789',
      email: 'priya@xyzplastics.com',
      message: 'Require ultrafine PCC for PVC pipe manufacturing',
      createdAt: '2025-02-19T14:20:00',
      status: 'contacted'
    }
  ];

  useEffect(() => {
    // Check if already logged in
    const authToken = localStorage.getItem('adminToken');
    if (authToken === 'voltron_admin_authenticated') {
      setIsAuthenticated(true);
      loadInquiries();
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    
    // Simple authentication (in production, this should be backend-validated)
    if (credentials.username === 'admin' && credentials.password === 'voltron2025') {
      localStorage.setItem('adminToken', 'voltron_admin_authenticated');
      setIsAuthenticated(true);
      loadInquiries();
      toast.success("Login Successful", {
        description: "Welcome to Voltron Admin Panel",
      });
    } else {
      toast.error("Login Failed", {
        description: "Invalid credentials",
      });
    }
  };

  const loadInquiries = () => {
    setLoading(true);
    // In production, fetch from API
    setTimeout(() => {
      setInquiries(mockInquiries);
      setLoading(false);
    }, 500);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setInquiries([]);
  };

  const handleDeleteInquiry = (id) => {
    if (window.confirm('Are you sure you want to delete this inquiry?')) {
      setInquiries(inquiries.filter(inq => inq.id !== id));
      toast.success("Inquiry Deleted", {
        description: "The inquiry has been removed",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <Card className="w-full max-w-md border-2">
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              <span className="text-slate-900">VOLTRON</span>{' '}
              <span className="text-blue-600">CALCIUM</span>
            </CardTitle>
            <p className="text-center text-gray-600 mt-2">Admin Panel Login</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  className="mt-1"
                  placeholder="Enter password"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                Login
              </Button>
            </form>
            <p className="text-xs text-gray-500 text-center mt-4">
              Demo: admin / voltron2025
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">
              <span className="text-slate-900">VOLTRON</span>{' '}
              <span className="text-blue-600">ADMIN</span>
            </h1>
            <Button onClick={handleLogout} variant="outline">
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">Total Inquiries</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{inquiries.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">New Inquiries</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {inquiries.filter(i => i.status === 'new').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-semibold">This Month</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{inquiries.length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inquiries Table */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Inquiries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : inquiries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No inquiries yet</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Requirement</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.map((inquiry) => (
                      <TableRow key={inquiry.id}>
                        <TableCell className="text-sm">{formatDate(inquiry.createdAt)}</TableCell>
                        <TableCell className="font-semibold">{inquiry.name}</TableCell>
                        <TableCell>{inquiry.company}</TableCell>
                        <TableCell>{inquiry.productRequired}</TableCell>
                        <TableCell>{inquiry.monthlyRequirement}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="flex items-center gap-1 mb-1">
                              <Phone className="w-3 h-3" />
                              <span>{inquiry.phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span>{inquiry.email}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={inquiry.status === 'new' ? 'default' : 'secondary'}>
                            {inquiry.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedInquiry(inquiry)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteInquiry(inquiry.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inquiry Detail Modal */}
        {selectedInquiry && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50"
            onClick={() => setSelectedInquiry(null)}
          >
            <Card 
              className="max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <CardHeader>
                <CardTitle>Inquiry Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Name</Label>
                    <p className="font-semibold">{selectedInquiry.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Company</Label>
                    <p className="font-semibold">{selectedInquiry.company}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Product Required</Label>
                    <p className="font-semibold">{selectedInquiry.productRequired}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Monthly Requirement</Label>
                    <p className="font-semibold">{selectedInquiry.monthlyRequirement}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Phone</Label>
                    <p className="font-semibold">{selectedInquiry.phone}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Email</Label>
                    <p className="font-semibold">{selectedInquiry.email}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Message</Label>
                  <p className="mt-2 p-4 bg-gray-50 rounded border">{selectedInquiry.message}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Received On</Label>
                  <p className="font-semibold">{formatDate(selectedInquiry.createdAt)}</p>
                </div>
                <Button 
                  onClick={() => setSelectedInquiry(null)} 
                  variant="outline" 
                  className="w-full"
                >
                  Close
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
